'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** ceiling type
//***********************************************************************************
//***********************************************************************************

import { cn_building, cn_element_type, cn_element_type_visitor, CN_MATERIAL_TYPE_CEILING, CN_MATERIALS_TYPES, code_to_label } from '..';
import { cn_configuration, cn_configuration_choice, cn_configuration_line, cn_configuration_param, cn_configuration_param_group, cn_configuration_tab } from './cn_configuration';
import { cn_element_type_layer_material } from './cn_element_type_layer_material';
import { cn_material } from './cn_material';

export const CEILING_CATEGORY_LIST = [
    { label: 'Détaillé', code: 'detailed' }
];

export const CEILING_STRUCTURE_LIST = [
    { label: 'Suspendu', code: 'hanged' },
    { label: 'Auto-portant', code: 'selfbearing' }
];

export class cn_ceiling_type extends cn_element_type_layer_material {
    //***********************************************************************************
    /**
     * Constructor
     */
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_ceiling_type';

        //*** Serialized data */
        this.name = '';
        this.category = 'detailed';
        this.structure = 'hanged';
        this.layers = [cn_material.material_by_code(parent, "fiber_ceiling", 0.05)];
        this.vertical = true;

        this.user_physics = false;
    }

    //***********************************************************************************
    /**
     * returns default ceiling types
     * @param {cn_building} building
     * @returns {cn_ceiling_type[]}
     */
    static default_types(building) {
        var ceiling_types = [];

        var st = new cn_ceiling_type(building);
        ceiling_types.push(st);

        return ceiling_types;
    }

    //***********************************************************************************
    /**
     * Returns a clone of this
     * @returns {cn_element_type}
     */
    clone() {
        var c = new cn_ceiling_type();
        c.name = this.name;
        c.category = this.category;
        c.structure = this.structure;
        c.layers = [];
        for (var i in this.layers)
            c.layers.push(this.layers[i].clone());
        c.vertical = this.vertical;
        return c;
    }

    //***********************************************************************************
    //**** keys
    //***********************************************************************************
    model_keys() {
        return ['name', 'category', 'structure', 'layers', 'vertical'];
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        return this._serialize(false)
    }

    serialize_building_agnostic() {
        return this._serialize(true)
    }

    _serialize(building_agnostic) {
        var json = this.serialize_element();
        json.class_name = 'cn_ceiling_type';
        json.name = this.name;
        json.category = this.category;
        json.structure = this.structure;
        json.layers = [];
        for (var i in this.layers)
            json.layers.push(this.layers[i]._serialize(building_agnostic));
        json.vertical = this.vertical;

        return json;
    }

    static unserialize(json, building) {
        if (typeof (json.ID) != 'string') return false;
        if (json.class_name != 'cn_ceiling_type') return false;
        var wt = new cn_ceiling_type(building);
        wt.unserialize_element(json);

        if (typeof (json.name) == 'string')
            wt.name = json.name;

        if (typeof (json.category) == 'string')
            wt.category = json.category;

        if (typeof (json.structure) == 'string')
            wt.structure = json.structure;

        wt.layers = [];
        if (typeof (json.layers) == 'object' && json.layers.length > 0) {
            for (var i in json.layers) {
                var layer = cn_material.unserialize(json.layers[i], building);
                if (layer == null) continue;
                wt.layers.push(layer);
            }
        }

        if (wt.layers.length == 0)
            wt.layers = [cn_material.material_by_code(building, 'unknown', 0.05)];

        if (typeof (json.vertical) == 'boolean')
            wt.vertical = json.vertical;

        return wt;
    }

    //***********************************************************************************
    //**** return label
    //***********************************************************************************
    get_label() {
        if (this.is_null) return "Aucun";
        if (this.name != '') return this.name;
        let nm = this.layers[0].name;
        if (this.layers.length > 1)
            nm += ' isolé';
        nm += ' ' + (this.thickness * 100).toFixed(0) + 'cm';
        if (this.vertical) nm = nm + " + retour";
        return nm;
    }

    //***********************************************************************************
    //**** Returns description
    //***********************************************************************************

    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        var description = [];
        description.push({ label: 'Structure', value: code_to_label(this.structure, CEILING_STRUCTURE_LIST) });
        description.push({ label: 'Matériau', value: this.layers[0].name });
        if (this.layers.length > 1)
            description.push({ label: 'Isolant', value: this.layers[1].name });
        else
            description.push({ label: 'Isolant', value: "Aucun" });
        description.push({ label: 'Epaisseur totale', value: this.thickness * 100, decimals: 1, unit: 'cm' });
        description.push({ label: 'Retour', value: (this.vertical) ? "Oui" : "Aucun" });

        return description;
    }

    //***********************************************************************************
    //**** draw icon
    //***********************************************************************************
    draw_svg_icon(widtho, heighto) {
        if (this.is_null) return super.draw_svg_icon(widtho, heighto);
        var html = '';

        html += '<g transform=\'translate(' + (widtho / 2) + ',' + (heighto / 2) + ') rotate(-90) translate(' + (-0.5 * widtho) + ',' + (-0.5 * heighto) + ') \'>';

        var width = heighto;
        var height = widtho;

        html += `<rect x="${width / 2}" y="${0}" width="${width / 2}" height="${height * 0.8}" style="fill:rgb(200,200,200);" />`;


        if (this.structure == "hanged") {
            html += `<line x1="${width / 2}" y1="${height / 2}" x2="${width}" y2="${height / 2}" style="stroke:black; stroke-width: 3px;" />`;
            html += `<line x1="${width / 2}" y1="${height * 0.2}" x2="${width}" y2="${height * 0.2}" style="stroke:black; stroke-width: 3px;" />`;
        }

        var sz = [width, height * 0.8];
        var p0 = [0, 0];
        var p1 = [width, height * 0.8];
        var scale = width;
        p0[0] = sz[0] * (0.5 - this.thickness * 0.5);
        for (var i = 0; i < this.layers.length; i++) {
            p1[0] = p0[0];
            var vertical_line = '<line x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' style=\'stroke:black\' />';
            p1[0] += sz[0] * this.layers[i].thickness;
            html += this.layers[i].draw_material(p0, p1[0] - p0[0], p1[1] - p0[1], scale);
            html += vertical_line;
            p0[0] = p1[0];
            if (i == this.layers.length - 1)
                html += '<line x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' style=\'stroke:black\' />';
        }

        if (this.vertical) {
            html += `<line x1="${(width - scale * this.thickness) / 2}" y1="${height * 0.8}" x2="${width}" y2="${height * 0.8}" style="stroke:black;" />`;
        }

        html += '</g>';
        return html;
    }

    //***********************************************************************************
    //**** Get configuration
    //***********************************************************************************
    static configuration() {
        var configuration = new cn_configuration();

        //***********************************************
        //*** configuration
        var ceiling = new cn_configuration_tab('Faux-plafond', 'detailed');
        configuration.add_tab(ceiling);

        //*** structure
        var ceiling_structure = new cn_configuration_line('Structure', 'structure');
        ceiling.add_line(ceiling_structure);
        CEILING_STRUCTURE_LIST.forEach(structure => ceiling_structure.add_choice(new cn_configuration_choice(structure.label, structure.code)));

        //*** material
        var ceiling_material = new cn_configuration_line('Matériau', 'material');
        ceiling.add_line(ceiling_material);
        ceiling_material.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 100, 'cm', 1, 0.5));
        configuration['materials'] = CN_MATERIALS_TYPES.filter(material => material.type == CN_MATERIAL_TYPE_CEILING);
        configuration['materials'].forEach(material => {
            ceiling_material.add_choice(new cn_configuration_choice(material.name, material.code));
        });

        //*** insulator
        configuration['insulator_labels'] = ['Minéral', 'Naturel', 'Synthétique'];
        configuration['insulator_codes'] = ['insulator', 'wood_fiber', 'styrofoam'];

        var ceiling_insulator = new cn_configuration_line('Isolant', 'insulator');
        ceiling.add_line(ceiling_insulator);
        ceiling_insulator.add_param(new cn_configuration_param('Epaisseur', 'thickness', 1, 100, 'cm', 1, 0.5));
        ceiling_insulator.add_param(cn_configuration_param.choice_list('Type', 'insulator_type', configuration['insulator_labels']));
        ceiling_insulator.add_choice(new cn_configuration_choice('Aucun', 'none'));
        ceiling_insulator.add_choice(new cn_configuration_choice('Oui', 'yes'));

        //*** vertical
        var ceiling_vertical = new cn_configuration_line('Retour', 'vertical');
        ceiling.add_line(ceiling_vertical);
        ceiling_vertical.add_choice(new cn_configuration_choice('Aucun', 'none'));
        ceiling_vertical.add_choice(new cn_configuration_choice('Oui', 'yes'));

        //***********************************************
        //*** Default values
        var wt = new cn_ceiling_type();
        wt.fill_configuration(configuration);
        return configuration
    }

    //***********************************************************************************
    //**** Fill configuration
    //***********************************************************************************
    fill_configuration(config = null) {
        var configuration = (config) ? config : cn_ceiling_type.configuration();

        //*** find proper tab
        var configuration_tab = configuration.tabs[0];

        //*** structure
        var ceiling_structure = configuration_tab.get_line('structure');
        ceiling_structure.set_selection(this.structure);

        //*** material
        var ceiling_material = configuration_tab.get_line('material');
        const material = this.layers[0];
        ceiling_material.set_selection(material.material_type.code);
        ceiling_material.set_param('thickness', material.thickness * 100);

        //*** insulator
        var ceiling_insulator = configuration_tab.get_line('insulator');
        if (this.layers.length < 2) {
            ceiling_insulator.set_selection('none');
            ceiling_insulator.set_param('thickness', 10, false);
            ceiling_insulator.get_param('insulator_type').visible = false;
        }
        else {
            const insulator = this.layers[1];
            ceiling_insulator.set_selection('yes');
            ceiling_insulator.set_param('thickness', insulator.thickness * 100, true);
            ceiling_insulator.set_param('insulator_type', configuration['insulator_codes'].indexOf(insulator.code), true);
        }

        //*** vertical
        var ceiling_vertical = configuration_tab.get_line('vertical');
        ceiling_vertical.set_selection(this.vertical ? "yes" : "none");

        return configuration;
    }

    //***********************************************************************************
    //**** Load configuration
    //***********************************************************************************
    load_configuration(configuration, actual_change = false) {
        var configuration_tab = configuration.tabs[configuration.selection];

        //*** structure
        var ceiling_structure = configuration_tab.get_line('structure');
        this.structure = ceiling_structure.get_selection();
        console.log("structure", ceiling_structure.get_selection(), CEILING_STRUCTURE_LIST.find(cs => cs.code == ceiling_structure.get_selection()).code);

        this.layers = [];

        var ceiling_material = configuration_tab.get_line('material');
        const material_type = configuration['materials'].find(mt => mt.code == ceiling_material.get_selection());
        const material_thickness = ceiling_material.get_param("thickness").value / 100;
        this.layers.push(cn_material.material_by_code(this.parent, material_type.code, material_thickness));

        var ceiling_insulator = configuration_tab.get_line('insulator');
        if (ceiling_insulator.get_selection() != "none") {
            const insulator_code = configuration['insulator_codes'][ceiling_insulator.get_param('insulator_type').value];
            const insulator_thickness = ceiling_insulator.get_param("thickness").value / 100;
            this.layers.push(cn_material.material_by_code(this.parent, insulator_code, insulator_thickness));
        }

        var ceiling_vertical = configuration_tab.get_line('vertical');
        this.vertical = (ceiling_vertical.get_selection() != "none");
        return true;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_ceiling_type(this);
    }
}

